import React, {useEffect} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {AuthCredentialType, ProfileEditVerifStatus} from 'types'
import {useDidUpdate, useTimer} from 'utils'
import {Button, Icon, Modal, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'

const StyledModal = styled(Modal)`
  box-sizing: border-box;
  width: 100%;
  max-width: ${convertUnit(335)};
  padding: ${convertUnit(20)};
  display: flex;
  align-items: center;
  text-align: center;
  flex-direction: column;
`
const StyledTitle = styled(Paragraph)`
  margin-bottom: ${convertUnit(8)};
`
const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: fill-available;
  margin-top: ${convertUnit(20)};
  gap: ${convertUnit(20)};
`
const StyledButton = styled(Button)`
  width: 100%;
`

interface GiftShopEditProfileModalVerifStatusProps {
  visible: boolean
  verifStatus: ProfileEditVerifStatus | undefined
  method: AuthCredentialType
  onPrimaryAction(): void
  loading?: boolean
}
export default function GiftShopEditProfileModalVerifStatus({
  visible,
  verifStatus,
  method,
  onPrimaryAction,
  loading,
}: GiftShopEditProfileModalVerifStatusProps) {
  const {translate} = useTranslation()
  const {countdown, reset} = useTimer({duration: 5, countOnStart: false})

  useEffect(() => {
    if (verifStatus === 'success') {
      reset()
    }
  }, [reset, verifStatus])

  useDidUpdate(() => {
    if (verifStatus === 'success' && countdown === 0) {
      onPrimaryAction()
    }
  }, [countdown])

  return (
    <StyledModal visible={visible} onBackdropPress={onPrimaryAction}>
      {verifStatus === 'too-many' ? (
        <>
          <StyledTitle fontSize="l" fontWeight="bold">
            {translate('giftShop:editCredentialVerifTMA')}
          </StyledTitle>
          <Paragraph fontSize="m">
            {translate('giftShop:editCredentialVerifTMAMessage')}
          </Paragraph>
        </>
      ) : (
        <>
          <Icon
            type={verifStatus === 'success' ? 'check' : 'close'}
            color={verifStatus === 'success' ? 'success_5' : 'gray_3'}
            size={48}
          />
          <StyledTitle fontSize="xl" fontWeight="bold">
            {translate(
              verifStatus === 'success' ? 'global:success' : 'global:failed',
            )}
          </StyledTitle>
          <Paragraph fontSize="l">
            {translate(
              verifStatus === 'failed'
                ? 'giftShop:editCredentialVerifFailed'
                : 'giftShop:editCredentialVerifSuccess',
              {context: method},
            )}
          </Paragraph>
          {verifStatus === 'success' && (
            <Paragraph>
              {translate('giftShop:editCredentialVerifSuccessCooldown', {
                time: countdown,
              })}
            </Paragraph>
          )}
        </>
      )}
      <StyledButtonContainer>
        <StyledButton
          label={translate(
            verifStatus === 'too-many'
              ? 'global:ok'
              : verifStatus === 'success'
              ? 'global:next'
              : 'global:tryAgain',
          )}
          isLoading={loading}
          onClick={onPrimaryAction}
        />
      </StyledButtonContainer>
    </StyledModal>
  )
}
